import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { useSingleTextMotion } from './motion'
import { cn } from '@/lib/utils'

type SingleTextProps = HTMLAttributes<HTMLDivElement> & {
  pretitle: string
  title: string
  text: string
  has_background: boolean
  alignment: 'left' | 'right' | 'center'
  className?: string
}

const SingleText = ({
  pretitle,
  title,
  text,
  alignment,
  has_background = false,
  ...props
}: SingleTextProps) => {
  const baseColClasses = 'col-span-4 md:col-span-5 lg:col-span-7 grid gap-spacing-sm'

  const backgroundClasses = has_background
    ? 'rounded-radius-minimal bg-white-light/20 px-spacing-sm py-spacing-lg md:px-spacing-lg'
    : ''

  const alignmentClasses = cn({
    'lg:col-span-6': alignment === 'left',
    'lg:col-span-8': alignment === 'left' && has_background,
    'md:col-span-4 md:col-start-2 lg:col-span-8 lg:col-start-3': alignment === 'center',
    'md:col-start-2 lg:col-span-6 lg:col-start-7': alignment === 'right',
    'md:col-start-2 lg:col-span-8 lg:col-start-5': alignment === 'right' && has_background
  })

  const { motionRef } = useSingleTextMotion()

  return (
    <Section ref={motionRef} data-single-text={`${alignment}_${has_background}`} {...props}>
      <Container>
        <Grid>
          <div className={cn(baseColClasses, backgroundClasses, alignmentClasses)}>
            {pretitle ? (
              <Typography variant="body" data-motion-pretitle>
                {pretitle}
              </Typography>
            ) : null}

            {title ? (
              <Typography
                variant="h6"
                component="h2"
                className={cn('[&_.line-mask]:-mt-2 [&_.word]:pt-2', {
                  'mix-blend-difference dark:text-text-heading-invert-light': !has_background
                })}
                data-motion-title
              >
                {title}
              </Typography>
            ) : null}

            {text ? (
              <Typography variant="body" className="mt-0 md:mt-spacing-xs" data-motion-paragraph>
                {text}
              </Typography>
            ) : null}
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default SingleText
